import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../utils/apiClient';
import {
  Calendar, Users, Clock, Check, X, AlertTriangle, Filter, ChevronDown, ChevronUp,
  Home, Coffee, Edit, Settings, Sun, Moon, Star, Zap, Award, DollarSign,
  TrendingUp, Trophy, Gift, Heart, Sparkles, CalendarDays, CalendarRange
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, differenceInMinutes, getDay, subDays, startOfWeek, endOfWeek } from 'date-fns';
import { ja } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import ShiftCalendarEnhanced from './ShiftCalendarEnhanced';
import ShiftCalendar from './ShiftCalendar';
import PageContainer from '../layout/PageContainer';
import BreakTimeEditorV2 from './BreakTimeEditorV2';
import WorkPatternManager from './WorkPatternManager';

interface WageRule {
  id: number;
  name: string;
  type: string;
  amount: number;
  conditions: any;
  icon?: string;
  icon_color?: string;
  is_active: boolean;
}

interface Staff {
  id: number;
  display_name: string;
  role_level: number;
  is_owner?: number;
}

interface AttendanceRecord {
  id: number;
  staff_id: number;
  staff_name: string;
  work_date: string;
  clock_in: string;
  clock_out: string | null;
  work_hours: number;
  overtime_hours: number;
  break_start?: string | null;
  break_end?: string | null;
  break_hours?: number;
}

interface ShiftRecord {
  id: number;
  staff_id: number;
  staff_name: string;
  shift_date: string;
  start_time: string;
  end_time: string;
  shift_type: string;
}

const AttendanceReport: React.FC = () => {
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [staff, setStaff] = useState<Staff[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<number[]>([]);
  const [attendanceData, setAttendanceData] = useState<AttendanceRecord[]>([]);
  const [shiftData, setShiftData] = useState<ShiftRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'comparison'>('comparison');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [editingBreak, setEditingBreak] = useState<AttendanceRecord | null>(null);
  const [showBreakDetails, setShowBreakDetails] = useState<number | null>(null);
  const [showPatternManager, setShowPatternManager] = useState(false);
  const [calendarKey, setCalendarKey] = useState(0); // カレンダーの再読み込み用
  const [wageRules, setWageRules] = useState<WageRule[]>([]);
  const [periodFilter, setPeriodFilter] = useState<'week' | 'twoWeeks' | 'month'>('week'); // 期間フィルター

  useEffect(() => {
    fetchStaff();
    fetchWageRules();
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedStaff]);

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${getApiUrl()}/staff/list`);
      const staffList = response.data.data || [];
      setStaff(staffList);
      // デフォルトで全員選択
      setSelectedStaff(staffList.map((s: Staff) => s.id));
    } catch (error) {
      console.error('スタッフ取得エラー:', error);
    }
  };

  const fetchWageRules = async () => {
    try {
      const response = await axios.get(`${getApiUrl()}/wage-rules`);
      if (response.data) {
        setWageRules(response.data);
      }
    } catch (error) {
      console.error('時給ルール取得エラー:', error);
    }
  };

  const fetchData = async () => {
    if (selectedStaff.length === 0) return;
    
    setLoading(true);
    const start = format(startOfMonth(selectedMonth), 'yyyy-MM-dd');
    const end = format(endOfMonth(selectedMonth), 'yyyy-MM-dd');

    try {
      // 勤怠データ取得
      const attendanceResponse = await axios.get(`${getApiUrl()}/attendance/range`, {
        params: {
          start_date: start,
          end_date: end,
          staff_ids: selectedStaff.join(',')
        }
      });
      setAttendanceData(attendanceResponse.data);

      // シフトデータ取得
      const shiftResponse = await axios.get(`${getApiUrl()}/shifts-enhanced/published`, {
        params: {
          start_date: start,
          end_date: end
        }
      });
      setShiftData(shiftResponse.data.filter((s: ShiftRecord) => 
        selectedStaff.includes(s.staff_id)
      ));
    } catch (error) {
      console.error('データ取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleStaffSelection = (staffId: number) => {
    setSelectedStaff(prev => {
      if (prev.includes(staffId)) {
        return prev.filter(id => id !== staffId);
      }
      return [...prev, staffId];
    });
  };

  const selectAllStaff = () => {
    setSelectedStaff(staff.map(s => s.id));
  };

  const clearSelection = () => {
    setSelectedStaff([]);
  };

  const toggleRowExpand = (key: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // シフトと実績の比較判定
  const getAttendanceStatus = (shift: ShiftRecord, attendance?: AttendanceRecord) => {
    if (!attendance || !attendance.clock_in) {
      return { status: 'absent', icon: <X className="w-5 h-5 text-red-500" />, label: '欠勤' };
    }

    if (!attendance.clock_out) {
      return { status: 'working', icon: <Clock className="w-5 h-5 text-blue-500" />, label: '勤務中' };
    }

    // 時刻の比較（15分の許容範囲）
    const shiftStart = new Date(`2000-01-01 ${shift.start_time}`);
    const shiftEnd = new Date(`2000-01-01 ${shift.end_time}`);
    const actualStart = new Date(attendance.clock_in);
    const actualEnd = new Date(attendance.clock_out);

    const startDiff = Math.abs(differenceInMinutes(
      new Date(`2000-01-01 ${actualStart.toTimeString().slice(0, 5)}`),
      shiftStart
    ));
    const endDiff = Math.abs(differenceInMinutes(
      new Date(`2000-01-01 ${actualEnd.toTimeString().slice(0, 5)}`),
      shiftEnd
    ));

    if (startDiff <= 15 && endDiff <= 15) {
      return { status: 'good', icon: <Check className="w-5 h-5 text-green-500" />, label: '◎ 予定通り' };
    } else if (startDiff <= 30 && endDiff <= 30) {
      return { status: 'ok', icon: <Check className="w-5 h-5 text-yellow-500" />, label: '○ 許容範囲' };
    } else {
      return { status: 'warning', icon: <AlertTriangle className="w-5 h-5 text-orange-500" />, label: '△ 要確認' };
    }
  };

  const formatTime = (datetime: string | null) => {
    if (!datetime) return '-';
    return format(new Date(datetime), 'HH:mm');
  };

  // アイコンコンポーネントを取得
  const getIconComponent = (iconName?: string) => {
    const iconMap: { [key: string]: React.FC<any> } = {
      sun: Sun,
      moon: Moon,
      star: Star,
      zap: Zap,
      'alert-triangle': AlertTriangle,
      award: Award,
      'dollar-sign': DollarSign,
      'trending-up': TrendingUp,
      trophy: Trophy,
      gift: Gift,
      heart: Heart,
      coffee: Coffee,
      sparkles: Sparkles
    };
    
    return iconMap[iconName || ''] || null;
  };

  // 適用される時給ルールを取得
  const getApplicableWageRules = (date: string, clockIn?: string, clockOut?: string | null, staffName?: string) => {
    // カツ氏（オーナー）には時給ルールを適用しない
    if (staffName && (staffName === 'カツ' || staffName.includes('オーナー'))) {
      return [];
    }
    
    const workDate = new Date(date);
    const dayOfWeek = getDay(workDate);
    const dayNames = ['日', '月', '火', '水', '木', '金', '土'];
    const dayName = dayNames[dayOfWeek];
    
    return wageRules.filter(rule => {
      if (!rule.is_active) return false;
      
      // 曜日ベースのルール
      if (rule.type === 'weekend' && rule.conditions?.days) {
        return rule.conditions.days.includes(dayName);
      }
      
      // 時間ベースのルール
      if (rule.type === 'time' && clockOut && rule.conditions?.start_time) {
        const endHour = parseInt(new Date(clockOut).toTimeString().split(':')[0]);
        const ruleStartHour = parseInt(rule.conditions.start_time.split(':')[0]);
        return endHour >= ruleStartHour;
      }
      
      // 繁忙期手当は手動適用のため、ここでは自動適用しない
      if (rule.type === 'peak') {
        return false;
      }
      
      return false;
    });
  };

  const formatHours = (hours: number | null) => {
    if (!hours) return '-';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}時間${m}分`;
  };

  // 日付ごとにグループ化
  const groupedData = new Map<string, {
    shifts: ShiftRecord[],
    attendances: AttendanceRecord[]
  }>();

  // シフトデータをグループ化
  shiftData.forEach(shift => {
    if (!groupedData.has(shift.shift_date)) {
      groupedData.set(shift.shift_date, { shifts: [], attendances: [] });
    }
    groupedData.get(shift.shift_date)!.shifts.push(shift);
  });

  // 勤怠データをグループ化
  attendanceData.forEach(attendance => {
    if (!groupedData.has(attendance.work_date)) {
      groupedData.set(attendance.work_date, { shifts: [], attendances: [] });
    }
    groupedData.get(attendance.work_date)!.attendances.push(attendance);
  });

  // 日付でソート（新しい順）
  const allSortedDates = Array.from(groupedData.keys()).sort((a, b) => b.localeCompare(a));

  // 期間フィルターに基づいて日付をフィルタリング
  // 日付文字列を直接比較（YYYY-MM-DD形式なので文字列比較が可能）
  const today = format(new Date(), 'yyyy-MM-dd');
  let filteredDates = allSortedDates;
  let filteredAttendanceRecords = attendanceData;

  if (periodFilter === 'week') {
    // 今日から過去1週間
    const weekAgo = format(subDays(new Date(), 7), 'yyyy-MM-dd');
    filteredDates = allSortedDates.filter(date => {
      return date >= weekAgo && date <= today;
    });
    filteredAttendanceRecords = attendanceData.filter(record => {
      return record.work_date >= weekAgo && record.work_date <= today;
    });
  } else if (periodFilter === 'twoWeeks') {
    // 今日から過去2週間
    const twoWeeksAgo = format(subDays(new Date(), 14), 'yyyy-MM-dd');
    filteredDates = allSortedDates.filter(date => {
      return date >= twoWeeksAgo && date <= today;
    });
    filteredAttendanceRecords = attendanceData.filter(record => {
      return record.work_date >= twoWeeksAgo && record.work_date <= today;
    });
  }
  // 'month'の場合は全データを表示（フィルタリングなし）

  const sortedDates = filteredDates;

  return (
    <PageContainer>
        {/* ヘッダー */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Calendar className="h-6 w-6 text-blue-600" />
              勤怠管理
            </h1>
            <div className="flex gap-2">
              <button
                onClick={() => setShowPatternManager(!showPatternManager)}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center gap-2"
              >
                <Settings className="w-5 h-5" />
                勤務設定
              </button>
              <button
                onClick={() => navigate('/home')}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Home className="w-5 h-5" />
                ホーム
              </button>
            </div>
          </div>

          {/* 月選択 */}
          <div className="flex items-center gap-4 mb-4">
            <input
              type="month"
              value={format(selectedMonth, 'yyyy-MM')}
              onChange={(e) => setSelectedMonth(new Date(e.target.value + '-01'))}
              className="px-3 py-2 border border-gray-300 rounded-lg"
            />
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('comparison')}
                className={`px-4 py-2 rounded-lg ${
                  viewMode === 'comparison' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                予実比較
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 rounded-lg ${
                  viewMode === 'list' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                実績一覧
              </button>
            </div>
          </div>

          {/* スタッフフィルター */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Filter className="h-5 w-5 text-gray-600" />
                <span className="font-semibold">スタッフ絞り込み</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={selectAllStaff}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  全選択
                </button>
                <button
                  onClick={clearSelection}
                  className="text-sm text-gray-600 hover:text-gray-700"
                >
                  クリア
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {staff.map(s => (
                <button
                  key={s.id}
                  onClick={() => toggleStaffSelection(s.id)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    selectedStaff.includes(s.id)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <span className="flex items-center gap-1">
                    {selectedStaff.includes(s.id) && <Check className="h-3 w-3" />}
                    {s.display_name || s.username || `スタッフ${s.id}`}
                  </span>
                </button>
              ))}
            </div>
            {/* カツ氏専用ボタン */}
            {staff.some(s => s.is_owner === 1) && (
              <div className="mt-3 pt-3 border-t">
                <button
                  onClick={() => {
                    const katsuStaff = staff.find(s => s.is_owner === 1);
                    if (katsuStaff) {
                      setSelectedStaff([katsuStaff.id]);
                    }
                  }}
                  className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center gap-2 text-sm"
                >
                  <Trophy className="w-4 h-4" />
                  カツ氏のみ選択（全営業日勤務）
                </button>
              </div>
            )}
          </div>

          {/* 期間フィルター */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 mb-2">
                <CalendarRange className="h-5 w-5 text-gray-600" />
                <span className="font-semibold">表示期間</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPeriodFilter('week')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  periodFilter === 'week'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <CalendarDays className="h-4 w-4" />
                1週間
              </button>
              <button
                onClick={() => setPeriodFilter('twoWeeks')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  periodFilter === 'twoWeeks'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <CalendarRange className="h-4 w-4" />
                2週間
              </button>
              <button
                onClick={() => setPeriodFilter('month')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  periodFilter === 'month'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <Calendar className="h-4 w-4" />
                1ヶ月
              </button>
              {periodFilter !== 'month' && (
                <div className="ml-auto text-sm text-gray-500 flex items-center">
                  表示中: {sortedDates.length}日分
                </div>
              )}
            </div>
          </div>
        </div>

        {/* データ表示 */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <div className="mt-2 text-gray-600">データを読み込み中...</div>
            </div>
          ) : viewMode === 'comparison' ? (
            /* 予実比較ビュー */
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">日付</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">スタッフ</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">予定</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">実績</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">判定</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">勤務時間</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">残業</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDates.map(date => {
                    const data = groupedData.get(date)!;
                    const rowKey = date;
                    const isExpanded = expandedRows.has(rowKey);
                    
                    return (
                      <React.Fragment key={date}>
                        <tr 
                          className="border-b hover:bg-gray-50 cursor-pointer"
                          onClick={() => toggleRowExpand(rowKey)}
                        >
                          <td className="px-4 py-3 font-medium">
                            <div className="flex items-center gap-2">
                              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              {format(new Date(date), 'M/d(E)', { locale: ja })}
                            </div>
                          </td>
                          <td className="px-4 py-3" colSpan={6}>
                            <div className="text-sm text-gray-600">
                              {data.shifts.length}名予定 / {data.attendances.length}名出勤
                            </div>
                          </td>
                        </tr>
                        
                        {isExpanded && (
                          <>
                            {/* シフトベースで表示 */}
                            {data.shifts.map(shift => {
                              const attendance = data.attendances.find(a => a.staff_id === shift.staff_id);
                              const status = getAttendanceStatus(shift, attendance);
                              
                              const applicableRules = getApplicableWageRules(date, attendance?.clock_in, attendance?.clock_out, shift.staff_name);
                              
                              return (
                                <tr key={`${date}-${shift.staff_id}`} className="border-b bg-gray-50">
                                  <td className="px-8 py-2"></td>
                                  <td className="px-4 py-2">
                                    <div className="flex items-center gap-2">
                                      <span>{shift.staff_name}</span>
                                      {/* 時給ルールアイコン表示 */}
                                      {applicableRules.length > 0 && (
                                        <div className="flex items-center gap-1">
                                          {applicableRules.slice(0, 3).map(rule => {
                                            const IconComponent = getIconComponent(rule.icon);
                                            return IconComponent ? (
                                              <IconComponent 
                                                key={rule.id}
                                                className={`h-4 w-4 ${rule.icon_color || 'text-gray-600'}`}
                                                title={`${rule.name}: +${rule.amount}円`}
                                              />
                                            ) : null;
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 text-center">
                                    <span className="text-sm">
                                      {shift.start_time.slice(0, 5)} - {shift.end_time.slice(0, 5)}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 text-center">
                                    {attendance ? (
                                      <span className="text-sm">
                                        {formatTime(attendance.clock_in)} - {formatTime(attendance.clock_out)}
                                      </span>
                                    ) : (
                                      <span className="text-sm text-gray-400">-</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-center">
                                    <div className="flex items-center justify-center gap-1">
                                      {status.icon}
                                      <span className="text-sm">{status.label}</span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 text-center">
                                    <span className="text-sm">
                                      {attendance ? formatHours(attendance.work_hours) : '-'}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 text-center">
                                    <span className="text-sm">
                                      {attendance && attendance.overtime_hours > 0 
                                        ? formatHours(attendance.overtime_hours)
                                        : '-'}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                            
                            {/* シフトがないが出勤した人 */}
                            {data.attendances
                              .filter(a => !data.shifts.find(s => s.staff_id === a.staff_id))
                              .map(attendance => {
                                const applicableRules = getApplicableWageRules(date, attendance.clock_in, attendance.clock_out, attendance.staff_name);
                                
                                return (
                                  <tr key={`${date}-extra-${attendance.staff_id}`} className="border-b bg-yellow-50">
                                    <td className="px-8 py-2"></td>
                                    <td className="px-4 py-2">
                                      <div className="flex items-center gap-2">
                                        <span>{attendance.staff_name}</span>
                                        {/* 時給ルールアイコン表示 */}
                                        {applicableRules.length > 0 && (
                                          <div className="flex items-center gap-1">
                                            {applicableRules.slice(0, 3).map(rule => {
                                              const IconComponent = getIconComponent(rule.icon);
                                              return IconComponent ? (
                                                <IconComponent 
                                                  key={rule.id}
                                                  className={`h-4 w-4 ${rule.icon_color || 'text-gray-600'}`}
                                                  title={`${rule.name}: +${rule.amount}円`}
                                                />
                                              ) : null;
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  <td className="px-4 py-2 text-center">
                                    <span className="text-sm text-gray-400">予定なし</span>
                                  </td>
                                  <td className="px-4 py-2 text-center">
                                    <span className="text-sm">
                                      {formatTime(attendance.clock_in)} - {formatTime(attendance.clock_out)}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 text-center">
                                    <div className="flex items-center justify-center gap-1">
                                      <AlertTriangle className="w-5 h-5 text-yellow-500" />
                                      <span className="text-sm">予定外出勤</span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 text-center">
                                    <span className="text-sm">{formatHours(attendance.work_hours)}</span>
                                  </td>
                                  <td className="px-4 py-2 text-center">
                                    <span className="text-sm">
                                      {attendance.overtime_hours > 0 
                                        ? formatHours(attendance.overtime_hours)
                                        : '-'}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            /* 実績一覧ビュー */
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">日付</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">スタッフ</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">出勤</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">退勤</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">勤務時間</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">残業時間</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">中抜け</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">操作</th>
                  </tr>
	                </thead>
	                <tbody>
	                  {filteredAttendanceRecords
	                    .sort((a, b) => b.work_date.localeCompare(a.work_date))
	                    .map(record => {
	                      const applicableRules = getApplicableWageRules(record.work_date, record.clock_in, record.clock_out, record.staff_name);
	                      
	                      return (
                        <tr key={record.id} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3">
                            {format(new Date(record.work_date), 'M/d(E)', { locale: ja })}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span>{record.staff_name}</span>
                              {/* 時給ルールアイコン表示 */}
                              {applicableRules.length > 0 && (
                                <div className="flex items-center gap-1">
                                  {applicableRules.slice(0, 3).map(rule => {
                                    const IconComponent = getIconComponent(rule.icon);
                                    return IconComponent ? (
                                      <IconComponent 
                                        key={rule.id}
                                        className={`h-4 w-4 ${rule.icon_color || 'text-gray-600'}`}
                                        title={`${rule.name}: +${rule.amount}円`}
                                      />
                                    ) : null;
                                  })}
                                </div>
                              )}
                            </div>
                          </td>
                        <td className="px-4 py-3 text-center">{formatTime(record.clock_in)}</td>
                        <td className="px-4 py-3 text-center">
                          {record.clock_out ? formatTime(record.clock_out) : (
                            <span className="text-blue-600 font-semibold">勤務中</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">{formatHours(record.work_hours)}</td>
                        <td className="px-4 py-3 text-center">
                          {record.overtime_hours > 0 ? (
                            <span className="text-red-600 font-semibold">
                              {formatHours(record.overtime_hours)}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {record.break_hours && record.break_hours > 0 ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowBreakDetails(showBreakDetails === record.id ? null : record.id);
                              }}
                              className="text-orange-600 hover:text-orange-700 font-semibold"
                              title="クリックして詳細を表示"
                            >
                              {(() => {
                                const hours = Math.floor(record.break_hours);
                                const minutes = Math.round((record.break_hours - hours) * 60);
                                if (hours === 0) return `${minutes}分`;
                                if (minutes === 0) return `${hours}時間`;
                                return `${hours}時間${minutes}分`;
                              })()}
                            </button>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                          {showBreakDetails === record.id && record.break_start && record.break_end && (
                            <div className="absolute z-10 mt-1 p-2 bg-white border rounded-lg shadow-lg text-sm">
                              <div className="font-semibold mb-1">中抜け時間</div>
                              <div>{new Date(record.break_start).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })} ～ {new Date(record.break_end).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}</div>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => setEditingBreak(record)}
                            className="text-blue-600 hover:text-blue-700"
                            title="中抜け時間を編集"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
	              </table>
	              
	              {filteredAttendanceRecords.length === 0 && (
	                <div className="p-8 text-center text-gray-500">
	                  選択された期間・スタッフの勤怠データがありません
	                </div>
	              )}
	            </div>
	          )}
        </div>

        {/* 統計情報 */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">総勤務時間</div>
            <div className="text-2xl font-bold text-gray-800">
              {formatHours(attendanceData.reduce((sum, a) => sum + (a.work_hours || 0), 0))}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">総残業時間</div>
            <div className="text-2xl font-bold text-red-600">
              {formatHours(attendanceData.reduce((sum, a) => sum + (a.overtime_hours || 0), 0))}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">出勤率</div>
            <div className="text-2xl font-bold text-green-600">
              {shiftData.length > 0 
                ? Math.round((attendanceData.length / shiftData.length) * 100) + '%'
                : '-'}
            </div>
          </div>
        </div>

        {/* 勤務パターン設定 */}
        {showPatternManager && (
          <div className="mb-8">
            <WorkPatternManager 
              onPatternsUpdated={() => {
                // カレンダーを再読み込み
                setCalendarKey(prev => prev + 1);
              }}
            />
          </div>
        )}

        {/* シフトカレンダー統合 */}
        <div className="mt-8">
          <div className="bg-white rounded-lg shadow-lg p-6 mb-4">
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2 mb-4">
              <Calendar className="h-6 w-6 text-blue-600" />
              シフトカレンダー
            </h2>
            <div className="text-sm text-gray-600 mb-4">
              カレンダーの日付をクリックしてシフトを追加・編集できます
            </div>
          </div>
          
          {/* ShiftCalendarEnhancedコンポーネントを埋め込みモードで表示 */}
          <div className="shift-calendar-container">
            <ShiftCalendarEnhanced key={calendarKey} embedded={true} />
          </div>
        </div>

        {/* 中抜け編集モーダル */}
        {editingBreak && (
          <BreakTimeEditorV2
            attendanceId={editingBreak.id}
            workDate={editingBreak.work_date}
            staffName={editingBreak.staff_name}
            clockIn={editingBreak.clock_in}
            clockOut={editingBreak.clock_out}
            existingBreaks={
              editingBreak.break_start && editingBreak.break_end
                ? [{
                    start_time: new Date(editingBreak.break_start).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', hour12: false }),
                    end_time: new Date(editingBreak.break_end).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', hour12: false })
                  }]
                : []
            }
            onClose={() => setEditingBreak(null)}
            onSave={() => {
              setEditingBreak(null);
              fetchData();
            }}
          />
        )}
    </PageContainer>
  );
};

export default AttendanceReport;
